"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Circle, Wallet } from "lucide-react";
import { AeraButton } from "@aera/ui";
import { MoneyUndoConfirmSheet } from "./MoneyUndoConfirmSheet";
import type { Transaction, WorkerSchema } from "../types";
import {
  buildTransactionHistorySummary,
  fetchTransactionHistory,
  fetchTransactionUndoImpact,
  formatTransactionUndoBlockMessage,
  postTransactionUndoCorrection,
  type TransactionHistoryEntry,
  type TransactionUndoImpact,
} from "../utils/transactionHistory";
import { getDisplayText } from "../utils/display";
import { parseHistoryStatusDetailLine } from "../utils/historyDisplay";
import { StatusBadge } from "./StatusBadge";
import { HistoryActorAvatar } from "./HistoryActorAvatar";
import {
  HistoryAmountText,
  historyAmountToneClass,
  resolveHistoryEntryAmountTone,
  type HistoryAmountTone,
} from "../utils/historyAmountHighlight";
function splitStatuses(value: string): string[] {
  const parts = value
    .split(/[•·|,]/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : [value.trim()].filter(Boolean);
}

function entryCanUndo(entry: TransactionHistoryEntry): boolean {
  // Backend marks the newest eligible money op (allocate / unmatch / move / …).
  return (
    entry.reversible === true ||
    Boolean(entry.availableActions?.includes("undo_latest"))
  );
}

function CurrentSummaryCard({
  record,
  schema,
  amountTone,
}: {
  record: Transaction;
  schema: WorkerSchema;
  amountTone: HistoryAmountTone;
}) {
  const summary = useMemo(
    () => buildTransactionHistorySummary(record, schema),
    [record, schema],
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated)">
      <div className="flex items-center gap-2 border-b border-(--aera-dt-border-soft) bg-aera-white px-4 py-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-(--aera-dt-row-selected-surface) text-(--aera-dt-accent-hover)">
          <Wallet className="h-4 w-4" aria-hidden />
        </div>
        <h3 className="text-sm font-bold text-aera-charcoal">{summary.headline}</h3>
      </div>

      <div className="space-y-3 px-4 py-4">
        {summary.rows.map((row) => (
          <div
            key={row.label}
            className="flex items-start justify-between gap-4"
          >
            <span className="shrink-0 text-xs font-semibold text-aera-lightslate">
              {row.label}
            </span>
            {row.label === "สถานะ" ? (
              <div className="flex flex-wrap justify-end gap-1.5">
                {splitStatuses(row.value).map((status) => (
                  <StatusBadge key={status} status={status} />
                ))}
              </div>
            ) : (
              <span
                className={`text-right text-sm leading-snug ${
                  row.emphasize
                    ? `font-bold tabular-nums ${historyAmountToneClass(amountTone)}`
                    : "font-medium text-aera-gray"
                }`}
              >
                {row.value}
              </span>
            )}
          </div>
        ))}

        {summary.explanation.trim() ? (
          <p className="rounded-xl bg-aera-offwhite px-3 py-2.5 text-xs leading-relaxed text-aera-gray">
            {summary.explanation}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function HistoryEntryRow({
  entry,
  isLast,
  defaultAmountTone,
  canUndo,
  undoBusy,
  onRequestUndo,
}: {
  entry: TransactionHistoryEntry;
  isLast: boolean;
  defaultAmountTone: HistoryAmountTone;
  canUndo: boolean;
  undoBusy: boolean;
  onRequestUndo: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const details = entry.details ?? [];
  const hasDetails = details.length > 0;
  const amountTone = resolveHistoryEntryAmountTone(entry, defaultAmountTone);
  const showActor = Boolean(entry.actorId?.trim() && entry.actorId !== "—");
  const description = entry.description.trim();
  const title = entry.title.trim();
  // Prefer description as the main line when it already carries action + amounts.
  const headline =
    description && description !== title ? description : title || description;

  return (
    <div className="flex gap-3">
      <div className="flex w-4 shrink-0 flex-col items-center pt-1.5">
        <Circle
          className={`h-2 w-2 fill-current ${
            entry.isLatest ? "text-aera-amber" : "text-aera-lightgray"
          }`}
          aria-hidden
        />
        {!isLast ? (
          <div className="mt-1 w-px flex-1 bg-aera-lightgray/80" aria-hidden />
        ) : null}
      </div>

      <div className={`min-w-0 flex-1 ${isLast ? "pb-1" : "pb-4"}`}>
        <p className="text-sm font-semibold leading-snug text-aera-charcoal">
          <HistoryAmountText text={headline} tone={amountTone} />
        </p>

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-aera-lightslate">
          <span className="tabular-nums">{entry.occurredAt}</span>
          {showActor ? (
            <>
              <span aria-hidden>·</span>
              <HistoryActorAvatar
                actorId={entry.actorId}
                actorLabel={entry.actorName}
              />
            </>
          ) : null}
        </div>

        {canUndo ? (
          <div className="mt-2">
            <AeraButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={undoBusy}
              onClick={onRequestUndo}
            >
              {undoBusy ? "กำลังโหลด…" : "ย้อนกลับการแก้ไขล่าสุด"}
            </AeraButton>
          </div>
        ) : null}

        {hasDetails ? (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="mt-1.5 flex cursor-pointer items-center gap-0.5 text-[11px] font-medium text-aera-lightslate transition-colors hover:text-aera-charcoal"
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
              aria-hidden
            />
            {expanded ? "ซ่อนรายละเอียด" : "รายละเอียด"}
          </button>
        ) : null}

        {expanded ? (
          <ul className="mt-1.5 space-y-0.5 pl-0.5">
            {details.map((line) => {
              const parsed = parseHistoryStatusDetailLine(line);
              return (
                <li
                  key={line}
                  className="text-[11px] leading-relaxed text-aera-gray before:mr-1.5 before:text-aera-lightslate before:content-['•']"
                >
                  {parsed ? (
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      <span>{parsed.prefix}:</span>
                      {parsed.statuses.map((status) => (
                        <StatusBadge key={`${line}:${status}`} status={status} />
                      ))}
                    </span>
                  ) : (
                    <HistoryAmountText text={line} tone={amountTone} />
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

function undoPreviewDetailLines(
  impact: TransactionUndoImpact | null,
  busy: boolean,
): string[] {
  if (!impact) {
    return busy ? ["กำลังโหลดรายละเอียด…"] : ["จะย้อนกลับการแก้ไขล่าสุด"];
  }
  if (impact.blocked || !impact.ok) {
    return impact.summary_lines?.length
      ? impact.summary_lines
      : [formatTransactionUndoBlockMessage(impact)];
  }
  if (impact.summary_lines?.length) return impact.summary_lines;
  const lines: string[] = [];
  if (impact.description?.trim()) lines.push(impact.description.trim());
  for (const detail of impact.details || []) {
    const text = String(detail || "").trim();
    if (text) lines.push(text);
  }
  return lines.length ? lines : ["จะย้อนกลับการแก้ไขล่าสุด"];
}

export function TransactionHistoryTab({
  record,
  schema,
  workspaceId,
  onUndone,
}: {
  record: Transaction;
  schema: WorkerSchema;
  workspaceId: string;
  onUndone?: () => void | Promise<void>;
}) {
  const recordId = useMemo(
    () => getDisplayText(record.record_id),
    [record.record_id],
  );
  const ownerUserId = useMemo(
    () =>
      getDisplayText(record.owner_user_id).trim() ||
      getDisplayText(record.user_id).trim(),
    [record.owner_user_id, record.user_id],
  );
  const defaultAmountTone: HistoryAmountTone =
    schema.workerId === "bill-lin-expense" ? "expense" : "income";
  const undoEnabled = schema.workerId === "bill-lin-income";
  const [entries, setEntries] = useState<TransactionHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);
  const [, setUndoError] = useState<string | null>(null);
  const [undoPreview, setUndoPreview] = useState<{
    entry: TransactionHistoryEntry;
    impact: TransactionUndoImpact;
  } | null>(null);
  const [undoPreviewLoading, setUndoPreviewLoading] = useState(false);

  const [undoBlockedIds, setUndoBlockedIds] = useState<Set<string>>(
    () => new Set(),
  );

  const hideUndoForEntry = (entryId: string) => {
    setUndoBlockedIds((prev) => {
      const next = new Set(prev);
      next.add(entryId);
      return next;
    });
    setEntries((rows) =>
      rows.map((row) =>
        row.id === entryId
          ? {
              ...row,
              reversible: false,
              availableActions: (row.availableActions || []).filter(
                (action) => action !== "undo_latest",
              ),
            }
          : row,
      ),
    );
    // Drop sticky shell undo bar immediately (same as cancel / receivable history).
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("aera:clear-latest-money-undo", {
          detail: { reason: "history_undo", entryId },
        }),
      );
    }
  };

  const emitToast = (message: string) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("aera:toast", {
        detail: { message, variant: "error" },
      }),
    );
  };

  const reloadHistory = useCallback(async () => {
    if (!recordId?.trim() || !workspaceId?.trim()) {
      setEntries([]);
      return;
    }
    const rows = await fetchTransactionHistory(recordId, workspaceId);
    setEntries(rows);
  }, [recordId, workspaceId]);

  useEffect(() => {
    setUndoBlockedIds(new Set());
  }, [recordId]);

  useEffect(() => {
    if (!recordId?.trim() || !workspaceId?.trim()) {
      setEntries([]);
      setLoadError(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    void reloadHistory()
      .then(() => {
        if (cancelled) return;
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setEntries([]);
        setLoadError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [recordId, reloadHistory, workspaceId]);

  const handleRequestUndo = async (entry: TransactionHistoryEntry) => {
    if (!ownerUserId || !workspaceId?.trim()) {
      setUndoError("ไม่พบผู้ใช้หรือ workspace สำหรับย้อนกลับ");
      return;
    }
    setUndoPreviewLoading(true);
    setUndoBusy(true);
    setUndoError(null);
    try {
      const impact = await fetchTransactionUndoImpact({
        ownerUserId,
        targetOperationId: entry.id,
      });
      if (impact.blocked || !impact.ok) {
        const message = formatTransactionUndoBlockMessage(impact);
        hideUndoForEntry(entry.id);
        setUndoPreview(null);
        setUndoError(null);
        emitToast(message);
        return;
      }
      setUndoPreview({ entry, impact });
    } catch (err) {
      setUndoPreview(null);
      const message =
        err instanceof Error ? err.message : "โหลดผลกระทบการย้อนกลับไม่สำเร็จ";
      setUndoError(message);
      emitToast(message);
    } finally {
      setUndoBusy(false);
      setUndoPreviewLoading(false);
    }
  };

  const handleConfirmUndo = async () => {
    if (!undoPreview) return;
    if (!ownerUserId || !workspaceId?.trim()) {
      setUndoError("ไม่พบผู้ใช้หรือ workspace สำหรับย้อนกลับ");
      return;
    }
    if (undoPreview.impact.blocked || !undoPreview.impact.ok) {
      const message = formatTransactionUndoBlockMessage(undoPreview.impact);
      hideUndoForEntry(undoPreview.entry.id);
      setUndoPreview(null);
      setUndoError(null);
      emitToast(message);
      return;
    }
    setUndoBusy(true);
    setUndoError(null);
    try {
      await postTransactionUndoCorrection({
        ownerUserId,
        workspaceId,
        targetOperationId: undoPreview.entry.id,
      });
      hideUndoForEntry(undoPreview.entry.id);
      setUndoPreview(null);
      await reloadHistory();
      await onUndone?.();
    } catch (err) {
      setUndoError(err instanceof Error ? err.message : "ย้อนกลับไม่สำเร็จ");
    } finally {
      setUndoBusy(false);
    }
  };

  const previewLines = undoPreviewDetailLines(
    undoPreview?.impact ?? null,
    undoBusy || undoPreviewLoading,
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <CurrentSummaryCard record={record} schema={schema} amountTone={defaultAmountTone} />
        <div className="flex items-center justify-center rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) px-4 py-14">
          <p className="text-sm font-semibold text-aera-charcoal">กำลังโหลดประวัติ…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CurrentSummaryCard record={record} schema={schema} amountTone={defaultAmountTone} />

      {loadError ? (
        <div className="flex items-center justify-center rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) px-4 py-14">
          <p className="text-sm font-semibold text-aera-charcoal">โหลดประวัติไม่สำเร็จ</p>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex items-center justify-center rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) px-4 py-14">
          <p className="text-sm font-semibold text-aera-charcoal">ยังไม่มีประวัติ</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-(--aera-dt-border-soft) bg-aera-white px-4 py-4">
          <h3 className="mb-3 text-sm font-bold text-aera-charcoal">
            ประวัติ
          </h3>
          {entries.map((entry, index) => {
            const canUndo =
              undoEnabled &&
              !undoBlockedIds.has(entry.id) &&
              entryCanUndo(entry);
            return (
              <HistoryEntryRow
                key={entry.id}
                entry={entry}
                isLast={index === entries.length - 1}
                defaultAmountTone={defaultAmountTone}
                canUndo={canUndo}
                undoBusy={undoBusy || undoPreviewLoading}
                onRequestUndo={() => void handleRequestUndo(entry)}
              />
            );
          })}
        </div>
      )}

      <MoneyUndoConfirmSheet
        isOpen={undoPreview != null}
        onClose={() => {
          if (undoBusy) return;
          setUndoPreview(null);
        }}
        busy={undoBusy}
        title={
          undoPreview?.impact.title ||
          undoPreview?.entry.title ||
          "ย้อนกลับการแก้ไข"
        }
        detailLines={previewLines}
        metaLine={
          [
            undoPreview?.impact.channel_label,
            undoPreview?.impact.occurred_at,
          ]
            .filter(Boolean)
            .join(" · ") || null
        }
        blocked={Boolean(
          undoPreview?.impact.blocked ||
            (undoPreview && !undoPreview.impact.ok),
        )}
        onConfirm={() => void handleConfirmUndo()}
      />
    </div>
  );
}
